import { Injectable, signal, computed } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

interface Failure {
  endpoint: string;
  timestamp: number;
  status: number;
}

interface FailedRequest {
  request: HttpRequest<unknown>;
  timestamp: number;
}

@Injectable({
  providedIn: 'root',
})
export class ServiceHealthService {
  private readonly apiUrl = `${window.location.origin}/api`;
  private readonly WINDOW_MS = 30000; // 30 seconds
  private readonly UNIQUE_ENDPOINT_THRESHOLD = 3;

  private failures = signal<Failure[]>([]);
  private _isDegraded = signal<boolean>(false);
  private _isTotalOutage = signal<boolean>(false);
  private _lastError = signal<HttpErrorResponse | null>(null);
  private _lastFailedRequest = signal<FailedRequest | null>(null);

  /** True when multiple failures detected within the window */
  readonly isDegraded = this._isDegraded.asReadonly();

  /** True when liveness check failed - complete service outage */
  readonly isTotalOutage = this._isTotalOutage.asReadonly();

  /** The most recent HTTP error */
  readonly lastError = this._lastError.asReadonly();

  /** Count of unique endpoints that failed in current window */
  readonly failedEndpointCount = computed(() => {
    const uniqueEndpoints = new Set(this.failures().map((f) => f.endpoint));
    return uniqueEndpoints.size;
  });

  constructor(private http: HttpClient) {}

  /**
   * Add a failure to the tracking window.
   * Called by HTTP interceptor on 5xx errors.
   */
  addFailure(endpoint: string, status: number, request?: HttpRequest<unknown>): void {
    const now = Date.now();

    // Clean up old failures outside the window
    const activeFailures = this.failures().filter(
      (f) => now - f.timestamp < this.WINDOW_MS
    );

    // Add new failure
    const updatedFailures = [
      ...activeFailures,
      { endpoint, timestamp: now, status },
    ];

    this.failures.set(updatedFailures);
    this._isDegraded.set(true);
    this._lastError.set(null); // Will be set by interceptor if needed

    // Store the failed request for potential retry
    if (request) {
      this._lastFailedRequest.set({ request, timestamp: now });
    }
  }

  /**
   * Check if the failure pattern indicates a total outage.
   * Returns true if 3+ unique endpoints failed AND liveness check fails.
   */
  async shouldRedirectToErrorPage(): Promise<boolean> {
    const uniqueEndpoints = this.getUniqueFailedEndpoints();

    // Not enough failures to indicate total outage
    if (uniqueEndpoints.size < this.UNIQUE_ENDPOINT_THRESHOLD) {
      return false;
    }

    // Multiple endpoints failed - check if service is actually down
    const isLive = await this.checkLiveness();
    if (!isLive) {
      this._isTotalOutage.set(true);
      return true;
    }

    // Service is running but multiple endpoints failed - partial outage
    return false;
  }

  /**
   * Check if the NestJS process is responding.
   * Returns true if /api/health/live returns 200, false otherwise.
   */
  async checkLiveness(): Promise<boolean> {
    try {
      await firstValueFrom(
        this.http.get<{ status: string }>(`${this.apiUrl}/health/live`, {
          // Skip interceptor for health checks to avoid infinite loops
        })
      );
      this._isTotalOutage.set(false);
      return true;
    } catch {
      this._isTotalOutage.set(true);
      return false;
    }
  }

  /**
   * Mark services as healthy. Called when liveness check succeeds.
   */
  markHealthy(): void {
    this._isDegraded.set(false);
    this._isTotalOutage.set(false);
    this.failures.set([]);
  }

  /**
   * Clear the failure window and reset state.
   */
  reset(): void {
    this.failures.set([]);
    this._isDegraded.set(false);
    this._isTotalOutage.set(false);
    this._lastError.set(null);
    this._lastFailedRequest.set(null);
  }

  /**
   * Get the last failed request for retry functionality.
   */
  getLastFailedRequest(): FailedRequest | null {
    return this._lastFailedRequest();
  }

  /**
   * Clear the last failed request after retry.
   */
  clearLastFailedRequest(): void {
    this._lastFailedRequest.set(null);
  }

  /**
   * Set the last HTTP error for display purposes.
   */
  setLastError(error: HttpErrorResponse): void {
    this._lastError.set(error);
  }

  /**
   * Get unique endpoints that failed within the current window.
   */
  private getUniqueFailedEndpoints(): Set<string> {
    const now = Date.now();
    const activeFailures = this.failures().filter(
      (f) => now - f.timestamp < this.WINDOW_MS
    );
    return new Set(activeFailures.map((f) => f.endpoint));
  }
}
